import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStore, faArrowLeft, faMapMarkerAlt, faPhone, faEnvelope,
  faStar, faBox, faSpinner, faShoppingBag, faCheckCircle,
  faUsers, faComments, faQuestionCircle, faUserPlus, faUserMinus,
  faThumbsUp
} from '@fortawesome/free-solid-svg-icons';
import { sellerAPI, productsAPI, userAPI, reviewsAPI, questionsAPI, followAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../assets/css/storeprofile.css';

const StoreProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');

  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [ratingStats, setRatingStats] = useState(null);

  // Q&A state
  const [storeQuestions, setStoreQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);

  const [stats, setStats] = useState({
    totalProducts: 0,
    totalSales: 0,
    rating: 0,
    responseRate: 0
  });

  // Fetch seller profile and products
  useEffect(() => {
    const fetchStoreData = async () => {
      try {
        setLoading(true);
        // userAPI.getById doesn't exist — this threw a TypeError on every
        // visit to any seller's storefront, silently swallowed by the empty
        // catch below, leaving the whole page blank with no error shown.
        // followAPI.getSellerProfile (GET /sellers/:id) is the real,
        // already-wired endpoint for this.
        const sellerResponse = userId
          ? await followAPI.getSellerProfile(userId)
          : await sellerAPI.getProfile();
        if (sellerResponse && sellerResponse.data) {
          const sellerData = sellerResponse.data.user || sellerResponse.data.seller || sellerResponse.data;
          setSeller(sellerData);
          setFollowersCount(
            sellerData.followers_count ?? sellerData.follower_count ?? sellerData.seller_followers ?? 0
          );
          setStats({
            totalProducts: sellerData.total_products || 0,
            totalSales: sellerData.total_sales || 0,
            rating: sellerData.rating || sellerData.average_rating || 4.5,
            responseRate: sellerData.response_rate || 95
          });
        }
      } catch {
      } finally {
        setLoading(false);
      }
    };

    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        // productsAPI.getBySeller doesn't exist either — same silent-blank
        // failure as the seller profile fetch above. The real products list
        // endpoint already supports filtering by seller_id.
        const productsResponse = userId
          ? await productsAPI.getAll({ seller_id: userId })
          : await sellerAPI.getProducts();
        if (productsResponse && productsResponse.data) {
          const productsList = Array.isArray(productsResponse.data)
            ? productsResponse.data
            : productsResponse.data.products || [];
          setProducts(productsList.filter(p =>
            p.status === 'active' || p.status === 'published' || !p.status
          ));
        }
      } catch {
      } finally {
        setProductsLoading(false);
      }
    };

    fetchStoreData();
    fetchProducts();
  }, [userId]);

  // Check follow status when user is logged in
  useEffect(() => {
    if (!userId || !isAuthenticated) return;
    const checkFollow = async () => {
      try {
        const r = await followAPI.isFollowing(userId);
        setIsFollowing(r?.data?.is_following ?? r?.is_following ?? false);
      } catch {}
    };
    checkFollow();
  }, [userId, isAuthenticated]);

  // Fetch seller reviews when tab becomes active
  useEffect(() => {
    if (activeTab !== 'reviews' || !userId || reviews.length > 0) return;
    const fetchReviews = async () => {
      setReviewsLoading(true);
      try {
        const r = await reviewsAPI.getPublicSellerReviews(userId);
        if (r?.data) {
          const list = Array.isArray(r.data) ? r.data : (r.data.reviews || []);
          setReviews(list);
          if (list.length > 0) {
            const avg = list.reduce((acc, rev) => acc + (rev.rating || 0), 0) / list.length;
            const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
            list.forEach(rev => {
              const s = Math.round(rev.rating || 0);
              if (s >= 1 && s <= 5) dist[s]++;
            });
            setRatingStats({ avg, total: list.length, distribution: dist });
          }
        }
      } catch {
      } finally {
        setReviewsLoading(false);
      }
    };
    fetchReviews();
  }, [activeTab, userId]);

  // Aggregate Q&A across seller's products when tab becomes active
  useEffect(() => {
    if (activeTab !== 'qa' || questionsLoaded || products.length === 0) return;
    const fetchQA = async () => {
      setQuestionsLoading(true);
      try {
        const results = await Promise.allSettled(
          products.slice(0, 10).map(p =>
            questionsAPI.getProductQuestions(p._id || p.id).then(r => ({
              productId: p._id || p.id,
              productName: p.title || p.name,
              questions: r?.data?.questions || r?.data || []
            }))
          )
        );
        const merged = results
          .filter(r => r.status === 'fulfilled')
          .flatMap(r =>
            r.value.questions.map(q => ({
              ...q,
              _productName: r.value.productName,
              _productId: r.value.productId
            }))
          );
        setStoreQuestions(merged);
        setQuestionsLoaded(true);
      } catch {
      } finally {
        setQuestionsLoading(false);
      }
    };
    fetchQA();
  }, [activeTab, products, questionsLoaded]);

  const handleFollowToggle = async () => {
    if (!isAuthenticated) { navigate('/auth'); return; }
    if (followLoading || !userId) return;
    try {
      setFollowLoading(true);
      if (isFollowing) {
        await followAPI.unfollowUser(userId);
        setIsFollowing(false);
        setFollowersCount(prev => Math.max(0, prev - 1));
      } else {
        await followAPI.followUser(userId);
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
      }
    } catch {
    } finally {
      setFollowLoading(false);
    }
  };

  const renderStars = (rating) =>
    [1, 2, 3, 4, 5].map(s => (
      <FontAwesomeIcon
        key={s}
        icon={faStar}
        style={{ color: s <= Math.round(rating) ? '#F59E0B' : '#D1D5DB', fontSize: '13px' }}
      />
    ));

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₦0';
    return `₦${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      const first = product.images[0];
      return typeof first === 'string' ? first : first?.url;
    }
    return product.image || 'https://via.placeholder.com/300?text=No+Image';
  };

  if (loading) {
    return (
      <div className="store-profile-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
        <p>Loading store profile...</p>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="store-profile-error">
        <FontAwesomeIcon icon={faStore} size="3x" />
        <h2>Store Not Found</h2>
        <p>The store you're looking for doesn't exist or has been removed.</p>
        <button onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="store-profile-container">
      {/* Header / Banner */}
      <div className="store-profile-header">
        <button className="store-back-btn" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        <div className="store-banner">
          <div className="store-banner-overlay"></div>
          <div className="store-info">
            <div className="store-avatar">
              <FontAwesomeIcon icon={faStore} />
            </div>
            <div className="store-details">
              <h1>{seller.shop_name || seller.business_name || 'Store Name'}</h1>
              <p className="store-location">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                {seller.shop_location || seller.location || 'Lagos, Nigeria'}
              </p>
              {seller.store_description && (
                <p className="store-description">{seller.store_description}</p>
              )}
              {/* Follow Button */}
              <button
                className={`sp-follow-btn ${isFollowing ? 'sp-follow-btn--following' : ''}`}
                onClick={handleFollowToggle}
                disabled={followLoading}
              >
                {followLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : isFollowing ? (
                  <><FontAwesomeIcon icon={faUserMinus} /> Following</>
                ) : (
                  <><FontAwesomeIcon icon={faUserPlus} /> Follow Store</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="store-stats">
        <div className="store-stat">
          <div className="store-stat-icon" style={{ background: '#22C55E20', color: '#22C55E' }}>
            <FontAwesomeIcon icon={faBox} />
          </div>
          <div className="store-stat-info">
            <h3>{stats.totalProducts}</h3>
            <p>Products</p>
          </div>
        </div>
        <div className="store-stat">
          <div className="store-stat-icon" style={{ background: '#F59E0B20', color: '#F59E0B' }}>
            <FontAwesomeIcon icon={faShoppingBag} />
          </div>
          <div className="store-stat-info">
            <h3>{stats.totalSales}+</h3>
            <p>Sales</p>
          </div>
        </div>
        <div className="store-stat">
          <div className="store-stat-icon" style={{ background: '#EF444420', color: '#EF4444' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
          <div className="store-stat-info">
            <h3>{stats.rating.toFixed(1)}</h3>
            <p>Rating</p>
          </div>
        </div>
        <div className="store-stat">
          <div className="store-stat-icon" style={{ background: '#3B82F620', color: '#3B82F6' }}>
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="store-stat-info">
            <h3>{stats.responseRate}%</h3>
            <p>Response Rate</p>
          </div>
        </div>
        <div className="store-stat">
          <div className="store-stat-icon" style={{ background: '#8B5CF620', color: '#8B5CF6' }}>
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="store-stat-info">
            <h3>{followersCount.toLocaleString()}</h3>
            <p>Followers</p>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      {(seller.email || seller.phone || seller.business_phone || seller.business_address) && (
        <div className="store-contact">
          <h2>Contact Information</h2>
          <div className="store-contact-grid">
            {seller.email && (
              <div className="store-contact-item">
                <FontAwesomeIcon icon={faEnvelope} />
                <span>{seller.email}</span>
              </div>
            )}
            {(seller.phone || seller.business_phone) && (
              <div className="store-contact-item">
                <FontAwesomeIcon icon={faPhone} />
                <span>{seller.phone || seller.business_phone}</span>
              </div>
            )}
            {seller.business_address && (
              <div className="store-contact-item">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>{seller.business_address}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="sp-tabs-wrap">
        <div className="sp-tabs-nav">
          <button
            className={`sp-tab-btn ${activeTab === 'products' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <FontAwesomeIcon icon={faBox} />
            Products
            <span className="sp-tab-count">{products.length}</span>
          </button>
          <button
            className={`sp-tab-btn ${activeTab === 'reviews' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            <FontAwesomeIcon icon={faStar} />
            Reviews
            {ratingStats && <span className="sp-tab-count">{ratingStats.total}</span>}
          </button>
          <button
            className={`sp-tab-btn ${activeTab === 'qa' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('qa')}
          >
            <FontAwesomeIcon icon={faComments} />
            Q&amp;A
            {storeQuestions.length > 0 && <span className="sp-tab-count">{storeQuestions.length}</span>}
          </button>
        </div>
      </div>

      {/* ── Tab: Products ── */}
      {activeTab === 'products' && (
        <div className="store-products-section">
          {productsLoading ? (
            <div className="store-products-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="store-products-empty">
              <FontAwesomeIcon icon={faBox} size="3x" />
              <h3>No Products Yet</h3>
              <p>This store hasn't listed any products.</p>
            </div>
          ) : (
            <div className="store-products-grid">
              {products.map(product => (
                <div
                  key={product._id || product.id}
                  className="store-product-card"
                  onClick={() => navigate(`/product/${product._id || product.id}`)}
                >
                  <div className="store-product-image">
                    <img src={getProductImage(product)} alt={product.title || product.name} />
                    {product.stock === 0 && (
                      <div className="store-product-badge out-of-stock">Out of Stock</div>
                    )}
                    {product.is_featured && (
                      <div className="store-product-badge featured">Featured</div>
                    )}
                  </div>
                  <div className="store-product-content">
                    <h3>{product.title || product.name}</h3>
                    <div className="store-product-price">
                      {product.discount_price && product.discount_price < product.price ? (
                        <>
                          <span className="store-product-price-discount">{formatCurrency(product.discount_price)}</span>
                          <span className="store-product-price-original">{formatCurrency(product.price)}</span>
                        </>
                      ) : (
                        <span className="store-product-price-regular">{formatCurrency(product.price)}</span>
                      )}
                    </div>
                    {product.rating && (
                      <div className="store-product-rating">
                        <FontAwesomeIcon icon={faStar} style={{ color: '#F59E0B' }} />
                        <span>{product.rating.toFixed(1)}</span>
                        {product.reviews_count && (
                          <span className="store-product-reviews">({product.reviews_count})</span>
                        )}
                      </div>
                    )}
                    <div className="store-product-footer">
                      <button className="store-product-btn">View Details</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Reviews ── */}
      {activeTab === 'reviews' && (
        <div className="sp-tab-content-wrap">
          {reviewsLoading ? (
            <div className="store-products-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Loading reviews...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="sp-empty-state">
              <FontAwesomeIcon icon={faStar} size="3x" />
              <h3>No Reviews Yet</h3>
              <p>This store hasn't received any reviews yet.</p>
            </div>
          ) : (
            <>
              {/* Rating overview */}
              {ratingStats && (
                <div className="sp-rating-overview">
                  <div className="sp-rating-big">
                    <span className="sp-rating-number">{ratingStats.avg.toFixed(1)}</span>
                    <div className="sp-rating-stars">{renderStars(ratingStats.avg)}</div>
                    <span className="sp-rating-total">{ratingStats.total} review{ratingStats.total !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="sp-rating-bars">
                    {[5, 4, 3, 2, 1].map(s => {
                      const count = ratingStats.distribution[s] || 0;
                      const pct = ratingStats.total > 0 ? (count / ratingStats.total) * 100 : 0;
                      return (
                        <div key={s} className="sp-rating-bar-row">
                          <span className="sp-rating-bar-label">{s} <FontAwesomeIcon icon={faStar} style={{ color: '#F59E0B', fontSize: '11px' }} /></span>
                          <div className="sp-rating-bar-track">
                            <div className="sp-rating-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="sp-rating-bar-count">({count})</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Review cards */}
              <div className="sp-reviews-list">
                {reviews.map((rev, i) => (
                  <div key={rev._id || rev.id || i} className="sp-review-card">
                    <div className="sp-review-header">
                      <div className="sp-review-avatar">
                        {(rev.reviewer_name || rev.user?.name || 'A')[0].toUpperCase()}
                      </div>
                      <div className="sp-review-meta">
                        <span className="sp-review-author">
                          {rev.reviewer_name || rev.user?.name || 'Anonymous'}
                        </span>
                        <div className="sp-review-stars">{renderStars(rev.rating || 0)}</div>
                      </div>
                      <span className="sp-review-date">
                        {rev.created_at
                          ? new Date(rev.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : 'Recently'}
                      </span>
                    </div>
                    {rev.comment && <p className="sp-review-comment">{rev.comment}</p>}
                    {rev.seller_response && (
                      <div className="sp-review-seller-reply">
                        <span className="sp-reply-label">Seller reply</span>
                        <p>{rev.seller_response}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Tab: Q&A ── */}
      {activeTab === 'qa' && (
        <div className="sp-tab-content-wrap">
          {questionsLoading ? (
            <div className="store-products-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Loading Q&amp;A...</p>
            </div>
          ) : storeQuestions.length === 0 ? (
            <div className="sp-empty-state">
              <FontAwesomeIcon icon={faQuestionCircle} size="3x" />
              <h3>No Questions Yet</h3>
              <p>No one has asked questions about this store's products yet.</p>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.7 }}>
                Browse a product and ask a question from the product page.
              </p>
            </div>
          ) : (
            <div className="sp-qa-list">
              {storeQuestions.map((q, i) => (
                <div key={q._id || q.id || i} className="sp-qa-card">
                  <div className="sp-qa-product-tag">
                    <FontAwesomeIcon icon={faBox} />
                    {q._productName}
                  </div>
                  <div className="sp-qa-question">
                    <span className="sp-qa-icon">❓</span>
                    <div className="sp-qa-body">
                      <p className="sp-qa-text">{q.question}</p>
                      <div className="sp-qa-footer">
                        <span className="sp-qa-author">
                          {q.user?.name || 'Anonymous'}
                        </span>
                        <span className="sp-qa-dot">·</span>
                        <span className="sp-qa-date">
                          {q.created_at
                            ? new Date(q.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Recently'}
                        </span>
                        {q.upvotes > 0 && (
                          <>
                            <span className="sp-qa-dot">·</span>
                            <span className="sp-qa-upvotes">
                              <FontAwesomeIcon icon={faThumbsUp} /> {q.upvotes}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {q.answer ? (
                    <div className="sp-qa-answer">
                      <span className="sp-qa-answer-label">Seller Answer</span>
                      <p>{q.answer}</p>
                    </div>
                  ) : (
                    <p className="sp-qa-no-answer">Awaiting seller response…</p>
                  )}
                  {/* Link to product */}
                  <button
                    className="sp-qa-view-product"
                    onClick={() => navigate(`/product/${q._productId}`)}
                  >
                    View Product
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreProfile;
